import React, { useState } from 'react';
import { NavLink, Outlet, useParams } from 'react-router-dom';
import {
    Box, Chip, CssBaseline, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Toolbar,
    Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FunctionsIcon from '@mui/icons-material/Functions';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminAppBar from './AdminAppBar';
import { useSeason } from '../../context/SeasonContext';

const DRAWER_WIDTH = 260;

function SidebarGroup({ label }) {
    return (
        <Typography
            variant="caption"
            sx={{
                display: 'block',
                px: 2.5,
                pb: 1.5,
                color: 'text.secondary',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
            }}
        >
            {label}
        </Typography>
    );
}

function SidebarItem({ to, icon, label, badge, end, onNavigate }) {
    return (
        <ListItemButton
            component={NavLink}
            to={to}
            end={end}
            onClick={onNavigate}
            sx={{
                height: 44,
                pl: 2.5,
                borderLeft: '3px solid transparent',
                '&.active': {
                    color: 'primary.main',
                    backgroundColor: 'rgba(254, 203, 0, 0.12)',
                    borderLeftColor: 'primary.main',
                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                    '& .MuiListItemText-primary': { fontWeight: 700 },
                },
            }}
        >
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{icon}</ListItemIcon>
            <ListItemText primary={label} slotProps={{ primary: { fontSize: 15 } }} />
            {badge != null && <Chip label={badge} size="small" sx={{ height: 20 }} />}
        </ListItemButton>
    );
}

/**
 * Layout van het seizoensbeheer: vaste sidebar van 260px op desktop, uitklapbare
 * drawer op mobiel. De navigatie volgt het ontwerp: een groep "Seizoen" met de
 * globale spelgegevens en een groep "Spel" met de subleagues en de beheerders.
 */
function AdminSeasonLayout({ subleagueCount }) {
    const { seasonId } = useParams();
    const { season } = useSeason();
    const [mobileOpen, setMobileOpen] = useState(false);

    const base = `/admin/season/${seasonId}`;
    const closeMobile = () => setMobileOpen(false);

    const nav = (
        <Box sx={{ py: 2 }}>
            <SidebarGroup label="Seizoen" />
            <List disablePadding>
                <SidebarItem to={`${base}/dashboard`} icon={<DashboardIcon />} label="Dashboard" onNavigate={closeMobile} />
                <SidebarItem to={`${base}/kalender`} icon={<CalendarMonthIcon />} label="Kalender & rondes" onNavigate={closeMobile} />
                <SidebarItem to={`${base}/stamgegevens`} icon={<SportsSoccerIcon />} label="Stamgegevens" onNavigate={closeMobile} />
                <SidebarItem to={`${base}/clubtransfers`} icon={<SwapHorizIcon />} label="Clubtransfers" onNavigate={closeMobile} />
                <SidebarItem to={`${base}/automatische-taken`} icon={<AutoModeIcon />} label="Automatische taken" onNavigate={closeMobile} />
                <SidebarItem to={`${base}/puntentelling`} icon={<FunctionsIcon />} label="Puntentelling" onNavigate={closeMobile} />
                <SidebarItem to={`${base}/instellingen`} icon={<SettingsIcon />} label="Instellingen" onNavigate={closeMobile} />
            </List>

            <Box sx={{ height: '1px', bgcolor: 'divider', mx: 2.5, my: 2 }} />

            <SidebarGroup label="Spel" />
            <List disablePadding>
                <SidebarItem
                    to={`${base}/subleagues`}
                    icon={<EmojiEventsIcon />}
                    label="Subleagues"
                    badge={subleagueCount}
                    onNavigate={closeMobile}
                />
                <SidebarItem to="/admin/beheerders" icon={<PeopleAltIcon />} label="Beheerders" onNavigate={closeMobile} />
            </List>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <CssBaseline />
            <AdminAppBar season={season} onOpenNav={() => setMobileOpen(true)} />

            <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={closeMobile}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
                    }}
                >
                    <Toolbar />
                    {nav}
                </Drawer>
                <Drawer
                    variant="permanent"
                    open
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': {
                            width: DRAWER_WIDTH,
                            boxSizing: 'border-box',
                            backgroundColor: 'rgba(255, 255, 255, 0.03)',
                            backgroundImage: 'none',
                            borderRight: '1px solid rgba(255, 255, 255, 0.15)',
                        },
                    }}
                >
                    <Toolbar />
                    {nav}
                </Drawer>
            </Box>

            <Box component="main" sx={{ flexGrow: 1, minWidth: 0, px: { xs: 2, md: 5 }, pb: 6 }}>
                <Toolbar />
                <Box sx={{ pt: 4 }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
}

export default AdminSeasonLayout;
